/* eslint-disable no-control-regex */
// Default fallback structure
export const defaultAIResult = {
  gender: null,
  primary_color: null,
  secondary_color: null,
  brand: null,
  category: null,
  material: null,
  extracted_outfit: {},
  suggested_outfit: {
    bottomwear: null,
    footwear: null,
    accessory: null,
  },
  image_data_extraction: {
    extracted_items: [],
    logo_detected: false,
  },
};

// Simple store for AI results without external dependencies
let currentAIResult = { ...defaultAIResult };

export const aiResultsStore = {
  getResult: () => currentAIResult,
  updateResult: (result: any) => {
    currentAIResult = { ...currentAIResult, ...result };
    return currentAIResult;
  },
  resetState: () => {
    currentAIResult = { ...defaultAIResult };
    return currentAIResult;
  },
};

// Safe parser for AI output
export const safeParseAIOutput = (jsonString: string) => {
  try {
    if (!jsonString || typeof jsonString !== 'string') {
      console.error('Invalid input to safeParseAIOutput:', jsonString);
      return { ...defaultAIResult };
    }

    // Extract JSON content if it's wrapped in markdown code blocks
    let cleanedString = jsonString;
    const jsonMatch = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
    if (jsonMatch && jsonMatch[1]) {
      cleanedString = jsonMatch[1];
    }

    // Clean up the string
    cleanedString = cleanedString.replace(/```json\n?|```/g, ''); // Remove code blocks
    cleanedString = cleanedString.replace(/#[^\n]*/g, ''); // Remove comments
    cleanedString = cleanedString.replace(/,(\s*[}\]])/g, '$1'); // Remove trailing commas
    cleanedString = cleanedString.replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3'); // Ensure property names are quoted
    cleanedString = cleanedString.replace(/:\s*'([^']*)'/g, ':"$1"'); // Replace single quotes with double quotes
    cleanedString = cleanedString.replace(/:\s*"([^"]*)\n([^"]*)"/g, ':"$1 $2"'); // Fix newlines in string values
    cleanedString = cleanedString.replace(/: (\d+\.\d+)/g, (_, p1) => {
      return `: ${parseFloat(p1).toFixed(3)}`;
    });

    // Handle specific error cases from the examples
    cleanedString = cleanedString.replace(/"color":\s*Suggest\s+th/g, '"color": "Suggest th"');

    // Handle bad control characters
    cleanedString = cleanedString.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
    // Extract key fields using regex if JSON parsing fails
    try {
      const parsedResult = JSON.parse(cleanedString);
      return { ...defaultAIResult, ...parsedResult };
    } catch (parseError) {
      console.error('JSON parsing failed, attempting to extract fields via regex:', parseError);

      // Extract fields using regex
      const result = { ...defaultAIResult };

      // Extract gender
      const genderMatch = jsonString.match(/"gender"\s*:\s*"?([^",}]+)"?/);
      if (genderMatch) result.gender = genderMatch[1].trim() || null;

      // Extract primary_color
      const primaryColorMatch = jsonString.match(/"primary_color"\s*:\s*"?([^",}]+)"?/);
      if (primaryColorMatch) result.primary_color = primaryColorMatch[1].trim() || null;

      // Extract brand
      const brandMatch = jsonString.match(/"brand"\s*:\s*"?([^",}]+)"?/);
      if (brandMatch && brandMatch[1].toLowerCase() !== 'null') result.brand = brandMatch[1].trim();

      // Extract category
      const categoryMatch = jsonString.match(/"category"\s*:\s*"?([^",}]+)"?/);
      if (categoryMatch) result.category = categoryMatch[1].trim() || null;

      // Extract material
      const materialMatch = jsonString.match(/"material"\s*:\s*"?([^",}]+)"?/);
      if (materialMatch && materialMatch[1].toLowerCase() !== 'null')
        result.material = materialMatch[1].trim();

      return result;
    }
  } catch (error) {
    console.error('Failed to parse AI output:', error);
    return { ...defaultAIResult };
  }
};
